/**
 * Today Screen Component
 *
 * The main screen showing today's accomplishments and the entry form.
 * Celebration-focused design that makes logging feel rewarding.
 */

import { useState } from 'react';
import { useTodayEntries, type Entry } from '@have-done/shared';
import { EntryForm } from './EntryForm';
import { EntryCard, EntryListEmpty } from './EntryCard';
import { EditEntryModal } from './EditEntryModal';
import { StreakBadge } from './StreakBadge';

export function TodayScreen() {
  const entries = useTodayEntries();
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);

  // Format today's date nicely
  const formatTodayDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  // Loading state - skeleton mirrors actual layout structure exactly
  if (entries === undefined) {
    return (
      <div className="space-y-8 animate-pulse">
        {/* Header skeleton - matches actual header structure */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="h-8 w-24 bg-bg-tertiary rounded" />
            <div className="h-5 w-40 bg-bg-tertiary rounded mt-1" />
          </div>
          <div className="flex items-center gap-3">
            <div className="h-8 w-24 bg-bg-tertiary rounded-full" />
          </div>
        </header>

        {/* Form skeleton - matches actual form wrapper */}
        <section>
          <div className="rounded-xl bg-bg-secondary p-6 shadow-warm-sm">
            <div className="space-y-4">
              <div className="h-[84px] bg-bg-tertiary rounded-lg" />
              <div>
                <div className="h-4 w-16 bg-bg-tertiary rounded mb-2" />
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-9 w-20 bg-bg-tertiary rounded-lg" />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="h-5 w-32 bg-bg-tertiary rounded" />
                <div className="h-12 w-40 bg-bg-tertiary rounded-lg" />
              </div>
            </div>
          </div>
        </section>

        {/* Entries section skeleton */}
        <section>
          <div className="h-6 w-48 bg-bg-tertiary rounded mb-4" />
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-lg border-l-4 border-bg-tertiary bg-bg-secondary p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="h-5 w-20 bg-bg-tertiary rounded-full" />
                  <div className="h-6 w-6 bg-bg-tertiary rounded" />
                </div>
                <div className="h-5 w-3/4 bg-bg-tertiary rounded" />
                <div className="h-4 w-1/2 bg-bg-tertiary rounded mt-2" />
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  const entryCount = entries?.length ?? 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-semibold text-text-primary">Today</h2>
          <p className="text-text-secondary">{formatTodayDate()}</p>
        </div>
        <div className="flex items-center gap-3">
          <StreakBadge />
          {entryCount > 0 && (
            <span className="text-sm text-text-secondary">
              {entryCount} accomplishment{entryCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </header>

      {/* Entry form */}
      <section aria-labelledby="new-entry-heading">
        <h3 id="new-entry-heading" className="sr-only">
          Log new accomplishment
        </h3>
        <div className="rounded-xl bg-bg-secondary p-6 shadow-warm-sm">
          <EntryForm />
        </div>
      </section>

      {/* Today's entries */}
      <section aria-labelledby="todays-entries-heading">
        <h3
          id="todays-entries-heading"
          className="text-lg font-semibold text-text-primary mb-4"
        >
          Today's accomplishments
        </h3>

        {entries && entries.length > 0 ? (
          <div className="space-y-3">
            {(entries as Entry[]).map((entry: Entry) => (
              <EntryCard
                key={entry._id}
                entry={entry}
                onEdit={setEditingEntry}
              />
            ))}
          </div>
        ) : (
          <EntryListEmpty message="No accomplishments logged today" />
        )}
      </section>

      {/* Edit modal */}
      <EditEntryModal
        entry={editingEntry}
        isOpen={editingEntry !== null}
        onClose={() => setEditingEntry(null)}
      />
    </div>
  );
}
